import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import {
  cancelEvalRun,
  commentEvalFinding,
  gradeEvalFinding,
  resolveEvalFinding,
  startEvalRun,
  startResolutionCheck,
} from '@mutations/evalRuns';
import type { EvalFindingGrade, EvalFindingResolution } from '@root/domain/schemas/evals/eval-run.schema';
import axios from 'axios';
import { get } from 'lodash-es';

const RESOLUTION_TOAST: Record<EvalFindingResolution | 'REOPENED', string> = {
  RESOLVED: 'Suggestion resolved',
  DISMISSED: 'Suggestion dismissed',
  REOPENED: 'Suggestion reopened',
};

/**
 * The resolution-check guards answer with messages written for the user
 * ("The contract has not changed since the review…"), so the API's own
 * message is what belongs in the toast — `error.message` on an Axios
 * rejection is only ever "Request failed with status code 409".
 */
const extractServerMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    return get(error, 'response.data.message') || get(error, 'response.data.error.message') || fallback;
  }
  return error instanceof Error ? error.message : fallback;
};

interface UseEvalRunMutationsArgs {
  submissionId: string;
  onStarted?: (runId: string) => void;
  onChanged?: () => void;
}

/**
 * Thin wrapper around the eval-run mutation endpoints with toasts and
 * simple loading flags. Invalidation is the caller's responsibility —
 * they pass an `onChanged` callback so the controller can re-fetch the
 * list/detail queries via react-query.
 */
export function useEvalRunMutations({ submissionId, onStarted, onChanged }: UseEvalRunMutationsArgs) {
  const [isStarting, setIsStarting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isGrading, setIsGrading] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isCommenting, setIsCommenting] = useState(false);
  const [isStartingResolutionCheck, setIsStartingResolutionCheck] = useState(false);

  const onStart = useCallback(
    // No config id means "the champion" — the server resolves it, which is
    // the only way a broker (who cannot read the config list) starts a scan.
    async (evalConfigId?: string, evalConfigVersionNumber?: number) => {
      setIsStarting(true);
      try {
        const res = await startEvalRun(submissionId, {
          eval_config_id: evalConfigId,
          eval_config_version_number: evalConfigVersionNumber,
        });
        toast.success('Guidance scan started', { autoClose: 4_000 });
        onStarted?.(res.id);
        onChanged?.();
      } catch (err) {
        // 409 (duplicate run) and 429 (daily budget) both carry a message
        // written for the user — surface it rather than the Axios status line.
        toast.error(extractServerMessage(err, 'Failed to start evaluation'), { autoClose: 8_000 });
      } finally {
        setIsStarting(false);
      }
    },
    [onStarted, onChanged, submissionId],
  );

  const onCancel = useCallback(
    async (runId: string) => {
      setIsCancelling(true);
      try {
        await cancelEvalRun(submissionId, runId);
        toast.success('Guidance scan cancelled', { autoClose: 4_000 });
        onChanged?.();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to cancel evaluation');
      } finally {
        setIsCancelling(false);
      }
    },
    [onChanged, submissionId],
  );

  const onStartResolutionCheck = useCallback(
    async (runId: string) => {
      setIsStartingResolutionCheck(true);
      try {
        await startResolutionCheck(submissionId, runId);
        toast.success('Checking which suggestions your edits resolved', { autoClose: 4_000 });
        onChanged?.();
      } catch (err) {
        toast.error(extractServerMessage(err, 'Failed to start the resolution check'), { autoClose: 8_000 });
      } finally {
        setIsStartingResolutionCheck(false);
      }
    },
    [onChanged, submissionId],
  );

  const onGrade = useCallback(
    async (runId: string, findingId: string, grade: EvalFindingGrade | null, gradeNote?: string) => {
      setIsGrading(true);
      try {
        await gradeEvalFinding(submissionId, runId, {
          finding_id: findingId,
          grade,
          grade_note: gradeNote,
        });
        onChanged?.();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save grade');
      } finally {
        setIsGrading(false);
      }
    },
    [onChanged, submissionId],
  );

  const onResolve = useCallback(
    async (runId: string, findingId: string, resolution: EvalFindingResolution | null, resolutionNote?: string) => {
      setIsResolving(true);
      try {
        await resolveEvalFinding(submissionId, runId, {
          finding_id: findingId,
          resolution,
          resolution_note: resolutionNote,
        });
        toast.success(RESOLUTION_TOAST[resolution ?? 'REOPENED'], { autoClose: 4_000 });
        onChanged?.();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to update the finding');
      } finally {
        setIsResolving(false);
      }
    },
    [onChanged, submissionId],
  );

  const onComment = useCallback(
    async (runId: string, findingId: string, text: string) => {
      setIsCommenting(true);
      try {
        await commentEvalFinding(submissionId, runId, { finding_id: findingId, text });
        onChanged?.();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save comment');
      } finally {
        setIsCommenting(false);
      }
    },
    [onChanged, submissionId],
  );

  return {
    onStart,
    onCancel,
    onGrade,
    onResolve,
    onComment,
    onStartResolutionCheck,
    isStarting,
    isCancelling,
    isGrading,
    isResolving,
    isCommenting,
    isStartingResolutionCheck,
  };
}
